import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AuthCallback Component
 * Handles Google OAuth callback with session_id in URL fragment
 * Exchanges session_id for user session and redirects to dashboard
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // CRITICAL: Prevent double processing (React StrictMode runs effects twice)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('❌ No session_id found in URL');
          navigate('/');
          return;
        }

        console.log('🔄 Processing Google OAuth callback...');

        // Exchange session_id for user session
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/google/session`,
          { session_id: sessionId },
          { withCredentials: true } // Important: Send/receive cookies
        );

        if (response.data.success) {
          const user = response.data.user;
          console.log('✅ Google authentication successful');

          // Clear the session_id from URL
          window.history.replaceState(null, '', window.location.pathname);

          // Navigate to dashboard with user data
          navigate('/', { 
            replace: true,
            state: { user } 
          });
        } else {
          console.error('❌ Session exchange failed:', response.data.error);
          navigate('/');
        }
      } catch (error) {
        console.error('❌ OAuth callback error:', error);
        navigate('/');
      }
    };

    processCallback();
  }, [location.hash, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-pink-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500 mx-auto mb-4"></div>
        <p className="text-lg text-gray-700">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
